import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchActiveClientsReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";

const ActiveClientsReport = () => {
  const dispatch = useDispatch();
  const [daysBack, setDaysBack] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const {
    data: clients,
    loading,
    error,
  } = useSelector((state) => state.reports.activeClients);

  useEffect(() => {
    dispatch(fetchActiveClientsReport({ daysBack }));
  }, [dispatch, daysBack]);

  const columns = [
    {
      header: "Client Name",
      accessor: "name",
    },
    {
      header: "Email",
      accessor: "email",
    },
    {
      header: "Additional Emails",
      accessor: (row) =>
        row.additionalEmails.length > 0
          ? row.additionalEmails.join(", ")
          : "None",
    },
    {
      header: "Last Appointment",
      accessor: (row) =>
        row.lastAppointment
          ? format(new Date(row.lastAppointment), "MM/dd/yyyy")
          : "Never",
    },
    {
      header: "Next Appointment",
      accessor: (row) =>
        row.nextAppointment
          ? format(new Date(row.nextAppointment), "MM/dd/yyyy")
          : "None Scheduled",
    },
  ];

  const handleDaysChange = (e) => {
    setDaysBack(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getDownloadLabel = () => {
    if (daysBack === -1) return "Download Active Clients - All Time";
    if (daysBack === 0) return "Download Active Clients - Future Only";
    return `Download Active Clients - Last ${daysBack} Days`;
  };

  const handleDownload = () => {
    if (!clients || clients.length === 0) {
      alert("No data available to download.");
      return;
    }

    const data = clients.map((client) => ({
      Name: client.name,
      Email: client.email || "N/A",
      "Additional Emails":
        client.additionalEmails.length > 0
          ? client.additionalEmails.join(", ")
          : "None",
      "Last Appointment": client.lastAppointment
        ? format(new Date(client.lastAppointment), "MM/dd/yyyy")
        : "Never",
      "Next Appointment": client.nextAppointment
        ? format(new Date(client.nextAppointment), "MM/dd/yyyy")
        : "None Scheduled",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Active Clients");

    const fileName = `Active_Clients_Report_${
      daysBack === -1 ? "All_Time" : `${daysBack}_Days`
    }.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  // Calculate pagination
  const totalItems = Array.isArray(clients) ? clients.length : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData =
    clients?.slice(startIndex, startIndex + itemsPerPage) || [];

  return (
    <div className="space-y-4">
      {/* Days Back Filter and Download Button */}
      <div className="flex justify-between items-center">
        <div>
          <label
            htmlFor="daysBack"
            className="block text-sm font-medium text-gray-700">
            Show clients with appointments in the last:
          </label>
          <select
            id="daysBack"
            value={daysBack}
            onChange={handleDaysChange}
            className="mt-1 block w-48 rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option value={-1}>All Time</option> {/* New "All Time" option */}
            <option value={0}>Future appointments only</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white shadow-sm rounded-sm">
        <Table
          columns={columns}
          data={paginatedData}
          title="Active Clients Report"
          description="View active clients and their appointment history"
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalItems}
          onPageChange={handlePageChange}
          actions={[
            {
              label: getDownloadLabel(),
              onClick: handleDownload,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ActiveClientsReport;
